import { ee } from '../services/earth-engine';
import * as turf from '@turf/turf';

/**
 * Measuring, along one transect, how far each land cover reaches from the shoreline.
 * This is the measurement at the heart of both CSqueeze and EUSCoast: the transect is cut by the
 * shoreline, each side is buffered, and every land-cover polygon is intersected with it to say how
 * much of that cover the transect crosses on the land side and on the water side. The two modules
 * ask different questions of the answer - coastal squeeze between a rising sea and fixed
 * infrastructure, versus the urban growth of the European coast - but the geometry is one piece of
 * code, and it was present in both as a byte-identical copy.
 * Two copies of a measurement are two places to fix and one place to forget, which is how this
 * repository has repeatedly ended up with a correction applied to one module only. So it lives here
 * once. Both modules re-export it, so every import site that already reads it from
 * `algorithms/csqueeze` or `algorithms/euscoast` keeps working unchanged.
 * @module LAND_COVER_TRANSECTS
 */

const options = { units: 'meters' };

const landcovers = [
  "Mangue",
  "Vegetação",
  "Corpo d'água",
  "Intervenção humana",
  "Praia, Duna e Areal"
];

/**
 * @function landParsingTurf
 * @description Determines how a single land-cover polygon intersects a transect's two side
 * buffers, and (for the water land-cover) which side the water is on.
 * 1. Buffers each of the transect's two sides by 1 meter and intersects them with the land-cover
 *    polygon.
 * 2. For each side with an intersection, computes its distance from the transect center and from
 *    the shoreline.
 * 3. Picks the side with the longer intersection as the land-cover's "biggest side"; if the
 *    land-cover is water, records that side and its intersection point as the water side/point.
 * @param {Object} landcover - Land-cover feature, with `id` and `geometry`.
 * @param {Array<Array>} sidesCoords - [sideOneCoords, sideTwoCoords] transect side coordinates.
 * @param {Array<Number>} centerCoord - Transect center [lng, lat].
 * @param {Object} shoreline - Shoreline feature/geometry used to compute shoreline distances.
 * **Called by:
 * @see {landCoversIntersectionsTurf}
 * @memberof module:LAND_COVER_TRANSECTS
 * @returns {Object} Land-cover intersection result: label, name, classe, lengthSide, biggestSide,
 * intersectPoint, baseLineDistance, shoreLineDistance, waterSide, waterIntersectPoint.
 */
function landParsingTurf(landcover, sidesCoords, centerCoord, shoreline) {
  const isWater = landcover.id === 2;
  const center = turf.point(centerCoord);
  const sideOneTurf = turf.lineString(sidesCoords[0])
  const sideOne = turf.buffer(sideOneTurf, 1, options);
  const sideTwoTurf = turf.lineString(sidesCoords[1])
  const sideTwo = turf.buffer(sideTwoTurf, 1, options);
  const landcoverTurf = turf.feature(landcover.geometry)

  const intersectionSideOne = turf.intersect(sideOne, landcoverTurf);
  const intersectionSideTwo = turf.intersect(sideTwo, landcoverTurf);

  const intersectionData = {
    intersectionSideOneLength: intersectionSideOne ? turf.length(intersectionSideOne, options) : 0,
    intersectionSideTwoLength: intersectionSideTwo ? turf.length(intersectionSideTwo, options) : 0,
    intersectionSideOneCoordinatesFlatten: intersectionSideOne ? turf.getCoords(intersectionSideOne).flat(Infinity) : [],
    intersectionSideTwoCoordinatesFlatten: intersectionSideTwo ? turf.getCoords(intersectionSideTwo).flat(Infinity) : []
  };

  const lengthSide = [0, 0]
  const distancesFromBaseLine = [0, 0];
  const distancesFromShoreLine = [0, 0];
  const landCoverIntersectPoint = [null, null];
  let landCoverBiggestSide = -1;
  let waterSide = -1;
  let waterIntersectPoint = [0, 0];

  const oneCoordsFlatten = intersectionData.intersectionSideOneCoordinatesFlatten.reverse()
  const twoCoordsFlatten = intersectionData.intersectionSideTwoCoordinatesFlatten.reverse()

  /**
   * @function calcularDistancias
   * @description Records a side's intersection length and computes its intersection point's
   * distance from the transect center and from the shoreline.
   * @param {Number} index - Side index (0 or 1) being processed.
   * @param {Array<Number>} coordsFlatten - Flattened, reversed intersection coordinates for the side.
   * @param {Number} intersectionLength - Length of the side's intersection with the land-cover.
   * **Called by:
   * @see {landParsingTurf}
   * @memberof module:LAND_COVER_TRANSECTS
   * @returns {void}
   */
  function calcularDistancias(index, coordsFlatten, intersectionLength) {
    lengthSide[index] = intersectionLength;
    landCoverIntersectPoint[index] = [coordsFlatten[1], coordsFlatten[0]];

    if (landCoverIntersectPoint[index] !== null) {
      distancesFromBaseLine[index] = turf.distance(
          turf.point(landCoverIntersectPoint[index]),
          center,
          options
      );

      const pontoShoreLineProximo = turf.nearestPointOnLine(
          shoreline,
          turf.point(landCoverIntersectPoint[index]),
          options
      );

      distancesFromShoreLine[index] = turf.distance(
          turf.point(landCoverIntersectPoint[index]),
          pontoShoreLineProximo,
          options
      );
    } else {
      distancesFromBaseLine[index] = null;
      distancesFromShoreLine[index] = null;
    }
  }

  if (oneCoordsFlatten.length > 0 && twoCoordsFlatten.length > 0) {
    calcularDistancias(0, oneCoordsFlatten, intersectionData.intersectionSideOneLength);
    calcularDistancias(1, twoCoordsFlatten, intersectionData.intersectionSideTwoLength);

    landCoverBiggestSide = lengthSide[0] > lengthSide[1] ? 0 : 1;
  } else if (twoCoordsFlatten.length > 0) {
    calcularDistancias(1, twoCoordsFlatten, intersectionData.intersectionSideTwoLength);

    landCoverBiggestSide = 1;
  } else if (oneCoordsFlatten.length > 0) {
    calcularDistancias(0, oneCoordsFlatten, intersectionData.intersectionSideOneLength);

    landCoverBiggestSide = 0;
  }

  if (isWater) {
    waterSide = landCoverBiggestSide;
    waterIntersectPoint = landCoverIntersectPoint[landCoverBiggestSide];
  }

  const name = landcovers[landcover.id];
  const classe = landcovers[landcover.id];

  return {
    label: landcover.id,
    name: name,
    classe: classe,
    lengthSide: lengthSide,
    biggestSide: landCoverBiggestSide,
    intersectPoint: landCoverIntersectPoint,
    baseLineDistance: distancesFromBaseLine,
    shoreLineDistance: distancesFromShoreLine,
    waterSide: waterSide,
    waterIntersectPoint: waterIntersectPoint
  };
}

/**
 * @function extractShoreLineTurf
 * @description Extracts the shoreline as a line by intersecting the buffered baseline polygon
 * with the water land-cover geometry.
 * @param {Object} baseLine - Baseline buffer polygon (Turf feature).
 * @param {Object} waterGeometry - Water land-cover geometry.
 * **Called by:
 * @see {ProcessingPage} - the CSqueeze and EUSCoast processing pages.
 * @memberof module:LAND_COVER_TRANSECTS
 * @throws {Error} If the water geometry doesn't overlap the buffered baseline at all for this
 * year (e.g. too few/no water training polygons were drawn), since `turf.intersect` then returns
 * `null` and `turf.polygonToLine` would otherwise fail with an unhelpful internal error.
 * @returns {Object} Shoreline as a Turf LineString/MultiLineString feature.
 */
export const extractShoreLineTurf = (baseLine, waterGeometry) => {
  const waterTurf = turf.feature(waterGeometry);
  const intersection = turf.intersect(baseLine, waterTurf);
  if (!intersection) {
    throw new Error('No water/baseline intersection found — check the water training polygons for this year.');
  }
  return turf.polygonToLine(intersection);
}

/**
 * @function landCoversIntersectionsTurf
 * @description Computes, for a single transect, how every land-cover type intersects its two
 * sides.
 * 1. Determines which side of the transect is water (via the water land-cover's intersection).
 * 2. If water was found, replaces the corresponding side with a line from the transect center to
 *    the water intersection point, so land-cover distances are measured from the water's edge.
 * 3. Computes the land-cover intersection data for every land-cover type against the (possibly
 *    adjusted) sides.
 * @param {Object} transect - Transect descriptor, with `transect` (id), `sides`, and `center`.
 * @param {Array<Object>} landcoversdata - Land-cover features to intersect (one of which is water, id 2).
 * @param {Object} shoreLine - Shoreline feature used to compute shoreline distances.
 * @param {Number} year - Year this land-cover data belongs to.
 * @see {@link landParsingTurf}
 * **Called by:
 * @see {ProcessingPage} - the CSqueeze and EUSCoast processing pages.
 * @memberof module:LAND_COVER_TRANSECTS
 * @returns {Object} {id, year, center, completeLine, sides, waterSide, waterIntersectPoint, landCoversIntersections}
 */
export const landCoversIntersectionsTurf = (transect, landcoversdata, shoreLine, year) => {

  const sideOneCoords = transect.sides[0].coords.map(arr => Object.values(arr));
  const sideTwoCoords = transect.sides[1].coords.map(arr => Object.values(arr));

  let sideOne = sideOneCoords;
  let sideTwo = sideTwoCoords;

  let completeLine = [sideOne, sideTwo];
  const lineCenter = transect.center;

  const waters = landcoversdata.filter(function (l) { return l.id === 2 })
  const outputwater = waters.map(function (water) {
    return landParsingTurf(water, [sideOne, sideTwo], lineCenter, shoreLine)
  })[0]

  const waterSide = outputwater.waterSide;
  const waterIntersectPoint = outputwater.waterIntersectPoint;

  if (waterSide > -1) {
    if (waterSide === 0) {
      sideOne = sideTwoCoords
      sideTwo = [lineCenter, waterIntersectPoint]
      completeLine = [sideOne, sideTwo]
    }

    if (waterSide === 1) {
      sideOne = sideOneCoords
      sideTwo = [lineCenter, waterIntersectPoint]
      completeLine = [sideOne, sideTwo]
    }
  }

  const landcoversOutput = landcoversdata.map(landcover => {
    return landParsingTurf(landcover, [sideOne, sideTwo], lineCenter, shoreLine)
  });

  return {
    id: transect.transect,
    year,
    center: lineCenter,
    completeLine,
    sides: transect.sides,
    waterSide: waterSide,
    waterIntersectPoint: waterIntersectPoint,
    landCoversIntersections: landcoversOutput
  };

}

/**
 * @function trainAndClassify
 * @description Trains a random forest classifier on user-drawn training areas (tagged with a
 * `LandCover` property) and classifies the image's given bands.
 * @param {ee.Image} image - Image to classify.
 * @param {ee.FeatureCollection} classificationAreas - Training area features, tagged with `LandCover`.
 * @param {Array<String>} bands - Band names used as classifier input features.
 * **Called by:
 * @see {LandCover}
 * @memberof module:LAND_COVER_TRANSECTS
 * @returns {ee.Image} The classified image.
 */
export const trainAndClassify = (image, classificationAreas, bands) => {
  let trainingData = image
    .select(bands)
    .sampleRegions({
      collection: classificationAreas,
      properties: ["LandCover"],
      scale: 30,
    });

  let classifier = ee.Classifier.smileRandomForest(30).train({
    features: trainingData,
    classProperty: "LandCover",
    inputProperties: bands,
  });

  return image.select(bands).classify(classifier);
}
